const timeline = [
  {
    period: "Month 1–4",
    icon: "ri-drop-line",
    color: "text-yellow-400",
    bg: "bg-yellow-400/10",
    border: "border-yellow-400/30",
    title: "Water enters micro-cracks",
    desc: "Early-stage infiltration. Micro-cracks allow water entry. Minimal structural damage.",
    cost: "$5,000",
    costLabel: "injection fix",
    severity: "Early",
    severityColor: "bg-yellow-400/20 text-yellow-300",
  },
  {
    period: "Month 5–12",
    icon: "ri-alert-line",
    color: "text-orange-400",
    bg: "bg-orange-400/10",
    border: "border-orange-400/30",
    title: "Moisture reaches steel reinforcement",
    desc: "Water contacts rebar. Oxidation begins. Concrete bond starts to weaken.",
    cost: "$15,000",
    costLabel: "intervention",
    severity: "Moderate",
    severityColor: "bg-orange-400/20 text-orange-300",
  },
  {
    period: "Month 12–24",
    icon: "ri-error-warning-line",
    color: "text-red-400",
    bg: "bg-red-400/10",
    border: "border-red-400/30",
    title: "Rebar corrodes and expands",
    desc: "Corrosion causes rebar to expand, cracking surrounding concrete. Structural integrity compromised.",
    cost: "$50,000+",
    costLabel: "structural repair",
    severity: "Critical",
    severityColor: "bg-red-400/20 text-red-300",
  },
  {
    period: "Year 2+",
    icon: "ri-building-line",
    color: "text-red-600",
    bg: "bg-red-600/10",
    border: "border-red-600/30",
    title: "Visible spalling and code violations",
    desc: "Concrete spalls and falls. Building code violations. Potential evacuation order.",
    cost: "Evacuation",
    costLabel: "risk",
    severity: "Emergency",
    severityColor: "bg-red-600/20 text-red-400",
  },
];

interface CostSectionProps {
  onInspectionClick: () => void;
}

export default function CostSection({ onInspectionClick }: CostSectionProps) {
  return (
    <section id="cost" className="bg-[#f0f4f8] py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[#4a7fa5] font-bold uppercase tracking-widest text-sm mb-3">Cost of Waiting</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0a0e1a] max-w-3xl mx-auto leading-tight">
            Every Month You Wait,{" "}
            <span className="text-[#1a3a5c]">the Repair Cost Multiplies.</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12">
          {timeline.map((item, idx) => (
            <div
              key={item.period}
              className={`bg-white border ${item.border} rounded-lg p-6 relative`}
            >
              {/* Stage number */}
              <div className="absolute -top-3 left-6">
                <span className="bg-[#0a0e1a] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Stage {idx + 1}
                </span>
              </div>

              <div className={`w-12 h-12 flex items-center justify-center rounded-full ${item.bg} mb-4 mt-2`}>
                <i className={`${item.icon} ${item.color} text-2xl`}></i>
              </div>

              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">{item.period}</p>
              <h3 className="text-[#0a0e1a] font-bold text-sm mb-2 leading-snug">{item.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed mb-4">{item.desc}</p>

              <div className="border-t border-gray-100 pt-4 flex items-end justify-between">
                <div>
                  <span className={`text-2xl font-black ${item.color}`}>{item.cost}</span>
                  <p className="text-gray-400 text-xs">{item.costLabel}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${item.severityColor}`}>
                  {item.severity}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bold statement */}
        <div className="bg-[#0a0e1a] rounded-lg px-8 py-7 max-w-3xl mx-auto text-center mb-10">
          <p className="text-white text-lg md:text-xl font-bold leading-relaxed">
            &ldquo;The infiltration didn&apos;t get more expensive.{" "}
            <span className="text-[#4a7fa5]">The delay did.</span>&rdquo;
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={onInspectionClick}
            className="bg-[#1a3a5c] hover:bg-[#0f2a45] text-white font-bold px-8 md:px-10 py-4 rounded-md transition-all text-sm md:text-base w-full sm:w-auto shadow-lg"
          >
            Schedule a Free Engineering Inspection
          </button>
          <p className="text-gray-400 text-xs mt-3 uppercase tracking-widest">No Obligation. No Guesswork.</p>
        </div>
      </div>
    </section>
  );
}
